import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle, ChevronDown, Search, Users, Store, CreditCard, Shield, MessageCircle } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../context/LanguageContext';
import { SEO, jsonLdSchemas } from '../components/SEO';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

export default function FAQ() {
  const { language, t } = useLanguage();
  const [activeCategory, setActiveCategory] = useState('customers');
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const categories = [
    { id: 'customers', icon: Users, label: t('faq.categories.customers') },
    { id: 'beneficiaries', icon: Users, label: t('faq.categories.beneficiaries') },
    { id: 'merchants', icon: Store, label: t('faq.categories.merchants') },
    { id: 'payments', icon: CreditCard, label: t('faq.categories.payments') },
    { id: 'security', icon: Shield, label: t('faq.categories.security') },
  ];

  const faqData = {
    customers: [
      {
        q: language === 'es' ? '¿Cómo funciona PeCash?' : 'How does PeCash work?',
        a: language === 'es' 
          ? 'Usted crea una autorización de compra para un monto específico en un colmado participante. Su beneficiario recibe un enlace seguro y puede usar la autorización para comprar artículos del hogar en ese comerciante.'
          : 'You create a purchase authorization for a specific amount at a participating colmado. Your beneficiary receives a secure link and can use the authorization to purchase household goods at that merchant.',
      },
      {
        q: language === 'es' ? '¿Mi beneficiario puede retirar efectivo?' : 'Can my beneficiary withdraw cash?',
        a: language === 'es'
          ? 'No. PeCash proporciona acceso de compra en comerciantes participantes, no efectivo. Esto está diseñado para ayudar a las familias a asegurar que los fondos se utilicen para necesidades del hogar.'
          : 'No. PeCash provides purchasing access at participating merchants, not cash. This is by design to help families ensure funds are used for household needs.',
      },
      {
        q: language === 'es' ? '¿Cuánto tiempo tiene mi beneficiario para usar la autorización?' : 'How long does my beneficiary have to use the authorization?',
        a: language === 'es'
          ? 'Las autorizaciones son válidas por 30 días. Los códigos QR se actualizan cada 10 minutos por seguridad, pero la autorización permanece activa.'
          : 'Authorizations are valid for 30 days. QR codes refresh every 10 minutes for security, but the authorization remains active.',
      },
      {
        q: language === 'es' ? '¿Qué pasa si mi beneficiario no usa el monto completo?' : 'What happens if my beneficiary doesn\'t use the full amount?',
        a: language === 'es'
          ? 'El saldo restante permanece disponible hasta que la autorización expire o usted la cancele.'
          : 'The remaining balance stays available until the authorization expires or you cancel it.',
      },
      {
        q: language === 'es' ? '¿Puedo cancelar una autorización?' : 'Can I cancel an authorization?',
        a: language === 'es'
          ? 'Sí, puede cancelar autorizaciones no utilizadas desde su panel de control. Los montos ya gastados no pueden ser reembolsados.'
          : 'Yes, you can cancel unused authorizations from your dashboard. Amounts already spent cannot be refunded.',
      },
      {
        q: language === 'es' ? '¿Cómo sé que mi beneficiario recibió la autorización?' : 'How do I know my beneficiary received the authorization?',
        a: language === 'es'
          ? 'Usted recibirá una confirmación cuando la autorización sea enviada. Puede rastrear el estado en su panel de control.'
          : 'You\'ll receive a confirmation when the authorization is sent. You can track the status in your dashboard.',
      },
    ],
    beneficiaries: [
      {
        q: language === 'es' ? '¿Cómo recibo una autorización de compra?' : 'How do I receive a purchase authorization?',
        a: language === 'es'
          ? 'Recibirá un mensaje de su familiar con un enlace seguro. Haga clic en el enlace para ver su autorización de compra.'
          : 'You\'ll receive a message from your family member with a secure link. Click the link to view your purchase authorization.',
      },
      {
        q: language === 'es' ? '¿Cómo uso mi autorización en el colmado?' : 'How do I use my authorization at the colmado?',
        a: language === 'es'
          ? 'Muestre su código QR al comerciante. Ellos lo escanearán y usted aprobará el monto de la compra en su teléfono.'
          : 'Show your QR code to the merchant. They will scan it and you\'ll approve the purchase amount on your phone.',
      },
      {
        q: language === 'es' ? '¿Puedo usar parte de mi autorización y guardar el resto?' : 'Can I use part of my authorization and save the rest?',
        a: language === 'es'
          ? 'Sí, puede hacer múltiples compras hasta que se agote el saldo o expire la autorización.'
          : 'Yes, you can make multiple purchases until the balance is used up or the authorization expires.',
      },
      {
        q: language === 'es' ? '¿Qué hago si mi código QR no funciona?' : 'What do I do if my QR code doesn\'t work?',
        a: language === 'es'
          ? 'Intente refrescar la página. Si el problema continúa, contacte a su familiar o a soporte.'
          : 'Try refreshing the page. If the problem continues, contact your family member or support.',
      },
    ],
    merchants: [
      {
        q: language === 'es' ? '¿Cómo me convierto en un colmado participante?' : 'How do I become a participating colmado?',
        a: language === 'es'
          ? 'Visite nuestra página de registro de comerciantes y complete la solicitud. Verificaremos su información y le contactaremos sobre los próximos pasos.'
          : 'Visit our merchant registration page and complete the application. We\'ll verify your information and contact you about next steps.',
      },
      {
        q: language === 'es' ? '¿Necesito equipo especial?' : 'Do I need special equipment?',
        a: language === 'es'
          ? 'No se requiere equipo especial. Solo necesita un smartphone para escanear códigos QR y confirmar compras.'
          : 'No special equipment required. You just need a smartphone to scan QR codes and confirm purchases.',
      },
      {
        q: language === 'es' ? '¿Cuándo recibo mi liquidación?' : 'When do I receive my settlement?',
        a: language === 'es'
          ? '[PLACEHOLDER: Detalles de tiempo de liquidación pendientes]'
          : '[PLACEHOLDER: Settlement timing details pending]',
      },
      {
        q: language === 'es' ? '¿Qué tarifas pagan los comerciantes?' : 'What fees do merchants pay?',
        a: language === 'es'
          ? '[PLACEHOLDER: Estructura de tarifas para comerciantes pendiente]'
          : '[PLACEHOLDER: Merchant fee structure pending]',
      },
    ],
    payments: [
      {
        q: language === 'es' ? '¿Cuáles son las tarifas?' : 'What are the fees?',
        a: language === 'es'
          ? 'Nuestras tarifas comienzan en $3.09 para una compra de $10 y escalan hasta $5.04 para una compra de $200. Siempre verá la tarifa exacta antes de confirmar.'
          : 'Our fees start at $3.09 for a $10 purchase and scale up to $5.04 for a $200 purchase. You\'ll always see the exact fee before confirming.',
      },
      {
        q: language === 'es' ? '¿Cuáles son los límites de transacción?' : 'What are the transaction limits?',
        a: language === 'es'
          ? 'Actualmente, las autorizaciones de compra van de $10 a $200. Estos límites pueden cambiar a medida que expandimos el servicio.'
          : 'Currently, purchase authorizations range from $10 to $200. These limits may change as we expand the service.',
      },
      {
        q: language === 'es' ? '¿Qué métodos de pago aceptan?' : 'What payment methods do you accept?',
        a: language === 'es'
          ? 'Aceptamos tarjetas de débito y crédito de EE.UU. (Visa, Mastercard, American Express, Discover).'
          : 'We accept U.S. debit and credit cards (Visa, Mastercard, American Express, Discover).',
      },
      {
        q: language === 'es' ? '¿Cómo funcionan los tipos de cambio?' : 'How do exchange rates work?',
        a: language === 'es'
          ? 'Los tipos de cambio se aplican al momento del pago y están sujetos a condiciones del mercado. Verá el tipo de cambio exacto antes de confirmar.'
          : 'Exchange rates are applied at checkout and subject to market conditions. You\'ll see the exact rate before confirming.',
      },
    ],
    security: [
      {
        q: language === 'es' ? '¿Es seguro PeCash?' : 'Is PeCash secure?',
        a: language === 'es'
          ? 'Sí. Usamos encriptación bancaria, códigos QR que rotan, y nunca almacenamos números completos de tarjetas. Todas las transacciones requieren aprobación del beneficiario.'
          : 'Yes. We use bank-grade encryption, rotating QR codes, and never store full card numbers. All transactions require beneficiary approval.',
      },
      {
        q: language === 'es' ? '¿Por qué rotan los códigos QR?' : 'Why do QR codes rotate?',
        a: language === 'es'
          ? 'Los códigos QR se actualizan cada 10 minutos para prevenir uso no autorizado si alguien fotografía el código.'
          : 'QR codes refresh every 10 minutes to prevent unauthorized use if someone photographs the code.',
      },
      {
        q: language === 'es' ? '¿Qué pasa si alguien intenta usar mi autorización sin mi permiso?' : 'What happens if someone tries to use my authorization without my permission?',
        a: language === 'es'
          ? 'Todas las compras requieren la aprobación del beneficiario. Si no aprueba la transacción, el comerciante no puede completar la compra.'
          : 'All purchases require beneficiary approval. If you don\'t approve the transaction, the merchant cannot complete the purchase.',
      },
      {
        q: language === 'es' ? '¿Cómo protegen mis datos?' : 'How do you protect my data?',
        a: language === 'es'
          ? 'Encriptamos todos los datos en tránsito y en reposo. Solo personal esencial tiene acceso a información de clientes, y cumplimos con todas las regulaciones de protección de datos aplicables.'
          : 'We encrypt all data in transit and at rest. Only essential staff have access to customer information, and we comply with all applicable data protection regulations.',
      },
    ],
  };

  const toggleItem = (categoryId, index) => {
    const key = `${categoryId}-${index}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const filteredFAQs = searchQuery
    ? Object.entries(faqData).flatMap(([category, items]) =>
        items
          .filter(item => 
            item.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.a.toLowerCase().includes(searchQuery.toLowerCase())
          )
          .map((item, idx) => ({ ...item, category, idx }))
      )
    : faqData[activeCategory].map((item, idx) => ({ ...item, category: activeCategory, idx }));

  return (
    <Layout>
      <SEO
        title={language === 'es' ? 'Preguntas Frecuentes' : 'Frequently Asked Questions'}
        description={language === 'es' 
          ? 'Encuentre respuestas a las preguntas más comunes sobre PeCash.'
          : 'Find answers to common questions about PeCash - how it works, fees, security, and more.'}
        canonical="https://pecashglobal.com/faq"
        lang={language}
      />
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 py-16">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div {...fadeInUp} className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-500/20 rounded-full mb-6">
                <HelpCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {language === 'es' ? 'Preguntas Frecuentes' : 'Frequently Asked Questions'}
              </h1>
              <p className="text-slate-400 max-w-2xl mx-auto mb-8">
                {language === 'es' 
                  ? 'Encuentre respuestas a las preguntas más comunes sobre PeCash.'
                  : 'Find answers to the most common questions about PeCash.'}
              </p>
              
              {/* Search */}
              <div className="relative max-w-md mx-auto">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={language === 'es' ? 'Buscar preguntas...' : 'Search questions...'}
                  className="w-full pl-12 pr-4 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-6 py-12">
          {/* Category Tabs */}
          {!searchQuery && (
            <motion.div {...fadeInUp} className="flex flex-wrap gap-2 mb-8 justify-center">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    activeCategory === cat.id
                      ? 'bg-emerald-600 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  <cat.icon className="w-4 h-4" />
                  {cat.label}
                </button>
              ))}
            </motion.div>
          )}

          {/* FAQ Items */}
          <div className="space-y-4">
            {filteredFAQs.map((item, index) => {
              const key = `${item.category}-${item.idx}`;
              const isOpen = openItems[key];
              
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                >
                  <button
                    onClick={() => toggleItem(item.category, item.idx)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                  >
                    <span className="font-medium text-slate-900 pr-4">{item.q}</span>
                    <ChevronDown 
                      className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
                    />
                  </button>
                  
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="px-6 pb-4">
                          <p className="text-slate-600 leading-relaxed">{item.a}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>

          {/* Still have questions? */}
          <motion.div 
            {...fadeInUp}
            className="mt-12 text-center bg-emerald-50 border border-emerald-200 rounded-2xl p-8"
          >
            <MessageCircle className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-emerald-900 mb-2">
              {language === 'es' ? '¿Aún tiene preguntas?' : 'Still have questions?'}
            </h2>
            <p className="text-emerald-800 mb-6">
              {language === 'es' 
                ? 'Nuestro equipo de soporte está aquí para ayudarle.'
                : 'Our support team is here to help you.'}
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-full hover:bg-emerald-700 transition-colors"
            >
              {language === 'es' ? 'Contáctenos' : 'Contact Us'}
            </Link>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
}
